import { css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';

import { orgStyles as s } from '@/features/org/components/org.styles';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useHistory } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * Dropdown switcher mounted in the dashboard top bar.
 *
 * Displays the current org with an avatar (first letter) + name. Click
 * opens a popover listing all orgs the user is a member of, plus actions
 * for managing the active org and creating a new one. Selecting an org:
 *   1. Updates Redux + writes the activeOrg cookie (useOrgs.switchOrg)
 *   2. Pushes history to /o/:slug/dashboard so deep links carry context
 *
 * Closes on outside click, escape key, or selection.
 */

function avatarLetter(name: string): string {
  return (name || '?').trim().charAt(0).toUpperCase();
}

export default function OrgSwitcher({
  onOpenMembers,
  onOpenSettings,
  onCreateOrg,
}: {
  onOpenMembers?: () => void;
  onOpenSettings?: () => void;
  onCreateOrg?: () => void;
}) {
  const { orgs, activeOrg, switchOrg } = useOrgs();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = (slug: string) => {
    switchOrg(slug);
    setOpen(false);
    history.push(`/o/${slug}/dashboard`);
  };

  if (!activeOrg) {
    // Either still loading or the user has zero orgs. The post-signup
    // path always seeds a personal org; if we get here it's a transient
    // state — render a placeholder.
    return (
      <div className={css(s.switcher)} ref={ref}>
        <button type="button" className={css(s.switcherBtn)} disabled>
          <span className={css(s.switcherAvatar)}>·</span>
          <span>Loading…</span>
        </button>
      </div>
    );
  }

  return (
    <div className={css(s.switcher)} ref={ref}>
      <button type="button" className={css(s.switcherBtn)} onClick={() => setOpen((v) => !v)}>
        <span className={css(s.switcherAvatar)}>{avatarLetter(activeOrg.name)}</span>
        <span>{activeOrg.name}</span>
        <span className={css(s.switcherChevron)}>▾</span>
      </button>

      {open && (
        <div className={css(s.popover)} role="menu">
          {orgs.map((o) => (
            <div
              key={o.id}
              role="menuitem"
              className={css(s.popoverItem, o.slug === activeOrg.slug && s.popoverItemActive)}
              onClick={() => handlePick(o.slug)}
            >
              <span className={css(s.switcherAvatar)}>{avatarLetter(o.name)}</span>
              <span>{o.name}</span>
              <span className={css(s.popoverItemRole)}>{o.myRole}</span>
            </div>
          ))}

          <div className={css(s.popoverDivider)} />

          {onOpenMembers && (
            <div
              className={css(s.popoverAction)}
              onClick={() => {
                setOpen(false);
                onOpenMembers();
              }}
            >
              Members
            </div>
          )}
          {onOpenSettings && (
            <div
              className={css(s.popoverAction)}
              onClick={() => {
                setOpen(false);
                onOpenSettings();
              }}
            >
              Org settings
            </div>
          )}
          {onCreateOrg && (
            <div
              className={css(s.popoverAction)}
              onClick={() => {
                setOpen(false);
                onCreateOrg();
              }}
            >
              + Create new org
            </div>
          )}
        </div>
      )}
    </div>
  );
}
